import { useRef } from "react";
import { Mail, Phone, Send, ChefHat, Utensils, Heart } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const positions = [
  {
    icon: ChefHat,
    title: "Koch / Köchin",
    note: "Erfahrung mit griechischer Küche willkommen — aber keine Pflicht.",
  },
  {
    icon: Utensils,
    title: "Service & Bedienung",
    note: "Freundlicher Umgang mit Gästen, Voll- und Teilzeit möglich.",
  },
  {
    icon: Heart,
    title: "Küchenhilfe / Aushilfe",
    note: "Auch ohne Vorerfahrung — wir lernen Sie an.",
  },
];

const SUBJECT = encodeURIComponent("Bewerbung bei Niko's Grillhouse");
const BODY = encodeURIComponent(
  `Guten Tag Team Niko's Grillhouse,

ich möchte mich gerne bei Ihnen bewerben.

Name:
Telefon:
Gewünschte Position:
Verfügbar ab:
Kurze Vorstellung:

Mit freundlichen Grüßen
`
);
const MAILTO = `mailto:NikosGrillBochum@gmail.com?subject=${SUBJECT}&body=${BODY}`;

export default function Bewerben() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section
      id="bewerben"
      ref={ref}
      data-testid="bewerben-section"
      className="relative bg-[var(--niko-dark)] text-white py-24 md:py-32 overflow-hidden"
    >
      {/* warm background hint */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 80% 20%, var(--niko-gold), transparent 60%), radial-gradient(60% 60% at 15% 85%, var(--niko-blue), transparent 60%)",
        }}
      />
      <div className="grain pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
        {/* left column — invitation */}
        <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
          <p
            className="reveal divider-leaf max-w-[200px] mb-8"
            style={{ color: "var(--niko-gold)" }}
          >
            Karriere
          </p>
          <h2
            className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            data-testid="bewerben-headline"
          >
            Werden Sie Teil <br />
            unserer{" "}
            <span className="italic text-[var(--niko-gold)]">Familie</span>.
          </h2>
          <p className="reveal reveal-delay-2 mt-6 font-greek italic text-lg md:text-xl text-white/75 leading-relaxed max-w-md">
            Seit 44 Jahren ist Niko's Grillhouse mehr als nur ein
            Restaurant — wir sind ein Team, das mit Leidenschaft kocht und
            herzlich bedient.
          </p>
          <p className="reveal reveal-delay-3 mt-5 text-base text-white/65 leading-relaxed max-w-md">
            Wir suchen Menschen, die griechische Gastfreundschaft leben
            wollen — egal ob mit Erfahrung oder als Quereinsteiger.
          </p>

          <div className="reveal reveal-delay-4 mt-10 pt-8 border-t border-white/10 hidden lg:block">
            <p className="text-xs tracking-[0.24em] uppercase text-white/55 mb-4">
              Lieber direkt anrufen?
            </p>
            <a
              href="tel:+492327995454"
              data-testid="bewerben-call"
              className="flex items-center gap-3 text-2xl md:text-3xl font-display text-[var(--niko-gold)] hover:text-[var(--niko-gold-soft)] transition-colors"
            >
              <Phone className="w-5 h-5" />
              +49 2327 995454
            </a>
            <p className="mt-4 text-xs text-white/45 leading-relaxed max-w-sm">
              Fragen nach Anastasios — er nimmt sich gerne Zeit für ein
              kurzes Gespräch.
            </p>
          </div>
        </div>

        {/* right column — positions + CTAs */}
        <div className="lg:col-span-7">
          <ul className="reveal space-y-4 md:space-y-5">
            {positions.map((p, i) => (
              <li
                key={i}
                data-testid={`bewerben-position-${i}`}
                className="reveal flex gap-5 items-start p-6 md:p-7 rounded-sm bg-white/[0.03] border border-white/10 hover:border-[var(--niko-gold)]/50 hover:bg-white/[0.05] transition-all"
              >
                <div className="w-12 h-12 shrink-0 rounded-full bg-[var(--niko-gold)]/15 flex items-center justify-center text-[var(--niko-gold)]">
                  <p.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-white leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed">
                    {p.note}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="reveal reveal-delay-3 mt-10 p-7 md:p-8 rounded-sm border border-[var(--niko-gold)]/30 bg-[var(--niko-gold)]/[0.04]">
            <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--niko-gold)] mb-3 font-semibold">
              So bewerben Sie sich
            </p>
            <p className="font-greek italic text-lg md:text-xl text-white/85 leading-relaxed">
              Schicken Sie uns einfach eine kurze Mail mit Ihrem Namen,
              Telefonnummer und ein paar Worten zu Ihnen — wir melden uns
              innerhalb weniger Tage.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3.5">
              <a
                href={MAILTO}
                data-testid="bewerben-mail-cta"
                className="inline-flex flex-1 items-center justify-center gap-3 px-6 py-4 rounded-full bg-[var(--niko-gold)] text-[var(--niko-dark)] text-sm font-semibold tracking-[0.18em] uppercase hover:bg-[var(--niko-gold-soft)] transition-colors"
              >
                <Send className="w-4 h-4" />
                Bewerbung senden
              </a>
              <a
                href="tel:+492327995454"
                data-testid="bewerben-tel-cta"
                className="inline-flex flex-1 items-center justify-center gap-3 px-6 py-4 rounded-full border border-white/30 text-white text-sm font-medium tracking-[0.18em] uppercase hover:bg-white/10 hover:border-white/60 transition-all lg:hidden"
              >
                <Phone className="w-4 h-4" />
                Anrufen
              </a>
            </div>

            <p className="mt-5 flex items-center gap-2 text-xs text-white/45">
              <Mail className="w-3.5 h-3.5" />
              <span className="font-medium text-white/70">
                NikosGrillBochum@gmail.com
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
